'use client';

import Img from '@umodeapporg/ui/components/Form/ControlledImage/Img';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useTranslation } from 'react-i18next';

import { IContaAtiva } from '@/domain/auth/helpers/contaAtiva';
import { cn } from '@/lib/utils';

/** The placeholder mark while the account has neither a name nor a logo. */
const ICONE_SEM_NOME = 18;

/** Fixed height of the mark; the width follows the file, so nothing is ever cropped. */
const ALTURA = {
  sm: 'h-7',
  lg: 'h-10',
} as const;

interface IFabricaLogoProps {
  conta: IContaAtiva;
  /** `sm` in the header, `lg` on the Portal, which is the factory's shop window. */
  size: keyof typeof ALTURA;
  className?: string;
}

/**
 * The mark of the active account, as registered in the uMode dashboard, shown WHOLE: a logo is
 * usually horizontal, so it gets a fixed height and a free width instead of being squeezed into
 * a circle. The light plate behind it is what keeps a dark-ink logo readable in the dark theme.
 *
 * With no file (or one that fails to load) the initials take its place in the same box, so the
 * header never jumps.
 */
export function FabricaLogo({ conta, size, className }: IFabricaLogoProps) {
  const { t } = useTranslation('common');
  const altura = ALTURA[size];

  const iniciais = (
    <span
      className={cn(
        'flex min-w-7 items-center justify-center rounded-md bg-brand-soft px-2 text-xs font-bold tracking-wide text-brand-emphasis',
        altura,
        className,
      )}
    >
      {/* An account with no name has no initials either: a lone empty square reads as a broken
          image, so the placeholder says out loud that a mark is missing. */}
      {conta.iniciais === '' ? <MaterialSymbol icon="storefront" size={ICONE_SEM_NOME} aria-hidden /> : conta.iniciais}
    </span>
  );

  if (conta.logoUrl === null) {
    return iniciais;
  }

  return (
    <span className={cn('flex items-center justify-center rounded-md bg-surface px-1.5', altura, className)}>
      <Img
        src={conta.logoUrl}
        alt={t('identidade.logoAlt', { fabrica: conta.nome })}
        className={cn('w-auto max-w-full object-contain', altura)}
        unloader={iniciais}
      />
    </span>
  );
}
